<template>
  <b-modal
    id="add-payment-method"
    body-class="add-payment-method-popup"
    ref="paymentMethod"
    title
    centered
    hide-footer
    @shown="shownModal"
    @hidden="resetPaymentMethodData"
  >
    <h3 class="none-margin">Add credit or debit card</h3>
    <div v-if="offerMeshulamCard" class="card-type" role="radiogroup" aria-label="Card type">
      <label
        class="card-type__option"
        :class="{ 'card-type__option--active': cardType === 'meshulam' }"
      >
        <input v-model="cardType" type="radio" name="card-type" value="meshulam" />
        <span class="card-type__title">Meshulam card</span>
        <span class="card-type__text">Valid for most campaigns in Israel, which use Meshulam.</span>
      </label>
      <label
        class="card-type__option"
        :class="{ 'card-type__option--active': cardType === 'stripe' }"
      >
        <input v-model="cardType" type="radio" name="card-type" value="stripe" />
        <span class="card-type__title">Stripe card</span>
        <span class="card-type__text">Mostly good for campaigns worldwide.</span>
      </label>
    </div>
    <div v-show="cardType === 'meshulam'">
      <p class="stripeError">{{ meshulamError }}</p>
      <p class="meshulam-note">
        You will enter your card on Meshulam's secure page. Meshulam makes a 1 ₪ verification charge
        to save the card.
      </p>
      <div class="btn-wrap tw-flex tw-gap-4 tw-flex-wrap tw-justify-center">
        <button
          class="add bttn bttn--lg bttn--green"
          :disabled="meshulamLoading"
          @click="addMeshulamCard"
        >
          {{ meshulamLoading ? "Opening Meshulam…" : "Continue to Meshulam" }}
        </button>
      </div>
    </div>
    <div v-show="cardType === 'stripe'">
      <div class>
        <p class="stripeError" v-if="stripeError">{{ stripeError }}</p>
        <p class="stripeError" v-else></p>
      </div>
      <div class="form-group">
        <label class="form-group__label form-group__label--required"
          >Card Number, Expiry, Card CVC and ZIP Code</label
        >
      </div>
      <div class="form-group">
        <div class="credit-card" ref="card"></div>
      </div>
      <div class="btn-wrap tw-flex tw-gap-4 tw-flex-wrap tw-justify-center">
        <button class="add bttn bttn--lg bttn--green" @click="addPaymentMethod">Add</button>
        <button class="add bttn bttn--lg bttn--green" @click="resetPaymentMethodData">Reset</button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapState, mapActions } from "vuex";
import axios from "axios";
import urls from "@/constants/urls.js";
import { MESHULAM_RETURN_PARAM, rememberMeshulamRegistration } from "../meshulam-card.js";

export default {
  name: "add-payment-method",

  data() {
    return {
      stripe: undefined,
      card: undefined,
      stripeError: "",
      // "meshulam" or "stripe"; see offerMeshulamCard
      cardType: "stripe",
      meshulamLoading: false,
      meshulamError: "",
    };
  },

  computed: {
    ...mapState({
      publicKeyLive: (state) => state.stripe.publicKeyLive,
      showMeshulamCard: (state) => state.featureHide.showMeshulamCard,
      meshulamCardsAvailable: (state) => state.stripe.meshulamCardsAvailable,
      donorAccount: (state) => state.donorAccount.account,
    }),
    // Meshulam is offered to visitors from Israel, and only when the server says
    // a card added here would work.
    offerMeshulamCard() {
      return this.showMeshulamCard && this.meshulamCardsAvailable;
    },
  },

  watch: {
    publicKeyLive(value) {
      this.stripe = Stripe(value);
    },
    offerMeshulamCard(offered) {
      if (!offered && this.cardType === "meshulam") this.cardType = "stripe";
    },
  },

  mounted() {
    this.getPublicKeyLive();
    window.addEventListener("pageshow", this.onPageShow);
  },

  beforeDestroy() {
    window.removeEventListener("pageshow", this.onPageShow);
  },

  methods: {
    ...mapActions({
      getPublicKeyLive: "getPublicKeyLive",
    }),

    openModal() {
      this.$root.$emit("bv::show::modal", "add-payment-method");
    },
    hideModal() {
      this.$root.$emit("bv::hide::modal", "add-payment-method");
    },
    // onPageShow re-enables the Meshulam button when the browser restores this page
    // from its back/forward cache, as it was when the donor left for Meshulam.
    onPageShow(event) {
      if (event.persisted) this.meshulamLoading = false;
    },
    shownModal() {
      this.cardType = this.offerMeshulamCard ? "meshulam" : "stripe";
      this.meshulamLoading = false;
      this.meshulamError = "";

      const elements = this.stripe.elements();
      this.card = elements.create("card");
      this.card.mount(this.$refs.card);
    },
    addPaymentMethod() {
      this.stripe.createToken(this.card).then((result) => {
        if (result.error) {
          this.stripeError = result.error.message;
        } else {
          this.saveTokenId(result.token.id);
        }
      });
    },
    resetPaymentMethodData() {
      this.card.clear();
      this.stripeError = false;
    },
    meshulamReturnUrl(result) {
      const url = new URL(this.$router.resolve("/donor-payment").href, window.location.origin);
      url.searchParams.set(MESHULAM_RETURN_PARAM, result);
      return url.toString();
    },
    async addMeshulamCard() {
      this.meshulamLoading = true;
      this.meshulamError = "";

      const attributes = this.donorAccount?.attributes || {};
      const body = {
        full_name: [attributes.first_name, attributes.last_name].filter(Boolean).join(" "),
        email: attributes.email || "",
        phone: attributes.phone || "",
        success_url: this.meshulamReturnUrl("done"),
        cancel_url: this.meshulamReturnUrl("cancelled"),
      };

      try {
        const { data } = await axios.post(urls.donorCardsMeshulam.replace("/:cardId", ""), body);
        const registration = data.data;

        rememberMeshulamRegistration(registration.reference);
        window.location.assign(registration.redirect_url);
      } catch (e) {
        this.meshulamError =
          e.response?.data?.errors?.[0]?.message ||
          "Meshulam could not be opened. Please try again.";
        this.meshulamLoading = false;
      }
    },
    saveTokenId(tokenId) {
      const body = {
        data: {
          attributes: {
            token: tokenId,
          },
        },
      };

      axios
        .post(urls.donorCardsStripe.replace("/:cardId", ""), body)
        .then(() => {
          this.$store.dispatch("getCards");

          this.hideModal();
        })
        .catch((e) => {
          console.log(e);
          this.stripeError = e.response.data.error;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.add-payment-method-popup {
  background: #1c273999;
  color: #414359;
  .close {
    font-size: 2.5rem;
    line-height: 0.5;
    color: #107598;
    opacity: 1;
    width: 36px;
    height: 36px;
    padding: 5px;
    border-radius: 18px;
    margin-top: -5px;
    outline: none;
    &:hover {
      background: #10759824;
    }
  }
  .stripeError {
    height: 24px;
    color: red;
    text-align: center;
  }
  .modal-dialog {
    max-width: 800px;
  }
  .modal-body {
    h3 {
      font: 30px/1.4 "VitoExtended-Bold";
      margin-bottom: 30px;
      text-align: center;
    }
    .none-margin {
      margin-bottom: 0;
    }
  }
  .modal-content {
    background: url("https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/donor_dashboard/deco1.svg")
        top -40px left 5% no-repeat,
      url("https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/charidy_images/donor_dashboard/deco2.svg")
        top -50px right 20% no-repeat,
      #fff;
    .modal-header {
      background: none;
    }
    .modal-body {
      background: none;
    }
  }
  .input-wrap {
    flex-grow: 2;
  }
  .bttn--green {
    background: #00d39a;
    width: 180px;
    color: #fff;
  }
  .form-group {
    text-align: center;
    margin-bottom: 40px;
    .credit-card {
      width: 390px;
      margin: 5% auto;
    }
    label {
      display: block;
      color: #414359;
      font-size: 20px;
    }
    img {
      vertical-align: inherit;
    }
  }
  .btn-wrap {
    text-align: center;
  }
  .card-type {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin: 24px 0 8px;
    &__option {
      flex: 1 1 240px;
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 16px;
      border: 1px solid #e6eaed;
      border-radius: 8px;
      cursor: pointer;
      text-align: left;
      input {
        margin-bottom: 4px;
      }
      &--active {
        border-color: #107598;
        background: #f7fbff;
      }
    }
    &__title {
      font-size: 18px;
      color: #414359;
    }
    &__text {
      font-size: 14px;
      color: #6f7285;
    }
  }
  .meshulam-note {
    text-align: center;
    margin-bottom: 40px;
  }
  .custom-dropdown {
    .dropdown-toggle {
      background: #f7fbff;
      min-width: 100px;
      height: 44px;
      border-radius: 19px;
      border: 1px solid #e6eaed;
      color: #107598;
      &:not(:disabled):hover,
      &:not(:disabled):focus,
      &:not(:disabled):active {
        color: #107598;
        background: #f7fbff;
        border: 1px solid #107598;
      }
    }
    &.show > .dropdown-toggle {
      color: #107598;
      background: #f7fbff;
      border: 1px solid #107598;
    }
    .dropdown-menu {
      min-width: 100%;
    }
  }
}
</style>
